import type React from 'react';

import type { ToolbarProps } from './types';
import { getWeekDates } from './utils';

import * as styles from './calendar-scheduler.css';

const ChevronLeftIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="15,18 9,12 15,6"></polyline>
    </svg>
);

const ChevronRightIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="9,18 15,12 9,6"></polyline>
    </svg>
);

export const Toolbar: React.FC<ToolbarProps> = ({
    currentDate,
    viewMode,
    onPrevDay,
    onNextDay,
    onToday,
    onViewModeChange,
    enableWeekView = false,
}) => {
    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        }).format(date);
    };

    const formatWeekRange = (date: Date) => {
        const weekDates = getWeekDates(date);
        const start = weekDates[0] ?? date;
        const end = weekDates[weekDates.length - 1] ?? date;

        const formatter = new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
        });
        const startLabel = formatter.format(start);
        const endLabel = formatter.format(end);
        const year =
            start.getFullYear() !== end.getFullYear()
                ? `, ${start.getFullYear()}`
                : '';
        return `${startLabel} - ${endLabel}${year}`;
    };

    return (
        <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
                <h2 className={styles.toolbarTitle}>
                    {viewMode === 'week' ? formatWeekRange(currentDate) : formatDate(currentDate)}
                </h2>
                <div className={styles.toolbarNavigation}>
                    {enableWeekView && onViewModeChange ? (
                        <div
                            className={styles.toolbarSegmented}
                            data-testid="calendar-view-toggle"
                        >
                            <button
                                onClick={() => onViewModeChange('day')}
                                className={`${styles.toolbarSegmentButton} ${viewMode === 'day' ? styles.toolbarSegmentButtonActive : ''}`}
                                aria-pressed={viewMode === 'day'}
                                title="Today"
                                data-testid="calendar-view-day"
                            >
                                Today
                            </button>
                            <button
                                onClick={() => onViewModeChange('week')}
                                className={`${styles.toolbarSegmentButton} ${viewMode === 'week' ? styles.toolbarSegmentButtonActive : ''}`}
                                aria-pressed={viewMode === 'week'}
                                title="Week view"
                                data-testid="calendar-view-week"
                            >
                                Week
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={onToday}
                            className={styles.toolbarButton}
                            title="Jump to today"
                        >
                            Today
                        </button>
                    )}
                    <button
                        onClick={onPrevDay}
                        className={styles.toolbarIconButton}
                        aria-label={viewMode === 'week' ? 'Previous week' : 'Previous day'}
                    >
                        <ChevronLeftIcon />
                    </button>
                    <button
                        onClick={onNextDay}
                        className={styles.toolbarIconButton}
                        aria-label={viewMode === 'week' ? 'Next week' : 'Next day'}
                    >
                        <ChevronRightIcon />
                    </button>
                </div>
            </div>
        </div>
    );
};
